"use client";

import { useId } from "react";
import type { WeightTrainingTemplateData } from "../weight-training-template";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type WeightTrainingDisplayPreviewProps = {
  data: Record<string, unknown> | WeightTrainingTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * The squat rack under a single hard spot: a loaded barbell racked across the
 * whole frame (the focal point) with competition bumpers on both sleeves,
 * chalk drifting in the light, a red neon strip on the back wall and a wooden
 * lifting platform running under the participant pill. Hex dumbbells and a
 * kettlebell sit on the floor at the flanks.
 *
 * Motion: four chalk motes drift up through the spot — CSS transform and
 * opacity only, off under reduced motion.
 */
const WEIGHT_KEYFRAMES = `
@keyframes wt-chalk {
  0% { transform: translate3d(0, 0, 0); opacity: 0 }
  20% { opacity: 0.9 }
  100% { transform: translate3d(4px, -34px, 0); opacity: 0 }
}
.wt-chalk { animation: wt-chalk 5s ease-out infinite }
@media (prefers-reduced-motion: reduce) {
  .wt-chalk { animation: none; opacity: 0.5 }
}
`;

const CHALK = [
  { left: "44%", top: "52%", size: 3, delay: "0s" },
  { left: "56%", top: "60%", size: 2, delay: "1.3s" },
  { left: "50%", top: "46%", size: 2.5, delay: "2.4s" },
  { left: "39%", top: "64%", size: 2, delay: "3.4s" },
];

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `wt-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

const PLATE_COLORS = {
  red: ["#ff6b6b", "#d1172b", "#6e0a14"],
  blue: ["#6aaeff", "#1f63d6", "#0c2d6e"],
  yellow: ["#ffe58a", "#f2c230", "#8a6a06"],
} as const;

type PlateColor = keyof typeof PLATE_COLORS;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        {(Object.keys(PLATE_COLORS) as PlateColor[]).map((color) => {
          const [light, base, dark] = PLATE_COLORS[color];
          return (
            <linearGradient
              key={color}
              id={id(`plate-${color}`)}
              x1="0"
              x2="0"
              y1="0"
              y2="1"
            >
              <stop offset="0" stopColor={light} />
              <stop offset="0.4" stopColor={base} />
              <stop offset="1" stopColor={dark} />
            </linearGradient>
          );
        })}
        <linearGradient id={id("chrome")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#f4f6f9" />
          <stop offset="0.45" stopColor="#a9afb9" />
          <stop offset="1" stopColor="#4a4f58" />
        </linearGradient>
        <linearGradient id={id("upright")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#3b3e46" />
          <stop offset="0.35" stopColor="#24262c" />
          <stop offset="1" stopColor="#0f1013" />
        </linearGradient>
        <linearGradient id={id("beam")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#fff3df" stopOpacity="0.36" />
          <stop offset="0.75" stopColor="#fff3df" stopOpacity="0.07" />
          <stop offset="1" stopColor="#fff3df" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id("wood")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#a97842" />
          <stop offset="1" stopColor="#5e3a18" />
        </linearGradient>
        <radialGradient id={id("iron")} cx="0.35" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#6b6f78" />
          <stop offset="0.55" stopColor="#24262c" />
          <stop offset="1" stopColor="#0b0b0e" />
        </radialGradient>
      </defs>
    </svg>
  );
}

const BAR_Y = 94;

/** A bumper seen from slightly in front: band, then the face toward centre. */
function Plate({
  id,
  x,
  color,
  side,
  ry = 40,
}: {
  id: IdFn;
  x: number;
  color: PlateColor;
  side: "left" | "right";
  ry?: number;
}) {
  const face = side === "left" ? x + 3 : x - 3;
  return (
    <g>
      <rect
        x={x - 3}
        y={BAR_Y - ry}
        width="6"
        height={ry * 2}
        fill={PLATE_COLORS[color][2]}
      />
      <ellipse
        cx={face}
        cy={BAR_Y}
        rx="8"
        ry={ry}
        fill={`url(#${id(`plate-${color}`)})`}
      />
      <ellipse
        cx={face}
        cy={BAR_Y}
        rx="5.5"
        ry={ry * 0.72}
        fill="none"
        stroke="#000000"
        strokeOpacity="0.2"
        strokeWidth="1"
      />
      <ellipse
        cx={face}
        cy={BAR_Y}
        rx="2.4"
        ry="7"
        fill={`url(#${id("chrome")})`}
      />
      <path
        d={`M${face - 6} ${BAR_Y - ry * 0.55} Q${face - 3} ${BAR_Y - ry} ${face + 1} ${BAR_Y - ry + 0.5}`}
        stroke="#ffffff"
        strokeOpacity="0.55"
        strokeWidth="1.1"
        fill="none"
      />
    </g>
  );
}

function Rack({ id }: { id: IdFn }) {
  const holes = Array.from({ length: 20 }, (_, i) => 6 + i * 8);
  return (
    <svg
      className="absolute top-0 left-1/2 -translate-x-1/2"
      width="300"
      height="220"
      viewBox="0 0 300 220"
      aria-hidden="true"
      focusable="false"
    >
      {/* Lifting platform */}
      <polygon points="86,166 214,166 262,220 38,220" fill="#141518" />
      <polygon
        points="114,166 186,166 206,220 94,220"
        fill={`url(#${id("wood")})`}
      />
      <path
        d="M126 166 L118 220 M138 166 L134 220 M150 166 L150 220 M162 166 L166 220 M174 166 L182 220"
        stroke="#3d2610"
        strokeOpacity="0.45"
        strokeWidth="0.8"
      />
      <line
        x1="86"
        x2="214"
        y1="166"
        y2="166"
        stroke="#ffffff"
        strokeOpacity="0.12"
      />

      {/* Rear uprights */}
      {[102, 198].map((x) => (
        <rect
          key={`rear-${x}`}
          x={x - 3}
          y="18"
          width="6"
          height="146"
          fill="#121317"
        />
      ))}
      <rect x="99" y="150" width="102" height="5" fill="#15161a" />

      {/* Spot from overhead */}
      <polygon
        points="128,-4 172,-4 236,150 64,150"
        fill={`url(#${id("beam")})`}
      />
      <ellipse cx="150" cy="168" rx="70" ry="9" fill="#fff3df" opacity="0.1" />

      {/* Front uprights with hole rows */}
      {[86, 214].map((x) => (
        <g key={`front-${x}`}>
          <rect
            x={x - 5}
            y="-2"
            width="10"
            height="174"
            fill={`url(#${id("upright")})`}
          />
          <rect
            x={x - 5}
            y="-2"
            width="1.2"
            height="174"
            fill="#ffffff"
            opacity="0.18"
          />
          {holes.map((y) => (
            <rect
              key={`hole-${x}-${y}`}
              x={x - 1.2}
              y={y}
              width="2.4"
              height="3.2"
              rx="0.8"
              fill="#050506"
            />
          ))}
          <rect
            x={x - 7}
            y="168"
            width="14"
            height="5"
            rx="1.5"
            fill="#0d0e11"
          />
        </g>
      ))}

      {/* J-hooks */}
      <path
        d="M80 92 L80 101 L92 101"
        stroke="#d1172b"
        strokeWidth="3"
        fill="none"
      />
      <path
        d="M220 92 L220 101 L208 101"
        stroke="#d1172b"
        strokeWidth="3"
        fill="none"
      />

      {/* Barbell shaft + knurl */}
      <rect
        x="62"
        y={BAR_Y - 2}
        width="176"
        height="4"
        rx="2"
        fill={`url(#${id("chrome")})`}
      />
      <rect
        x="118"
        y={BAR_Y - 2}
        width="64"
        height="4"
        fill="#2a2d33"
        opacity="0.25"
      />
      {/* Sleeves */}
      <rect
        x="14"
        y={BAR_Y - 3.5}
        width="52"
        height="7"
        rx="2"
        fill={`url(#${id("chrome")})`}
      />
      <rect
        x="234"
        y={BAR_Y - 3.5}
        width="52"
        height="7"
        rx="2"
        fill={`url(#${id("chrome")})`}
      />

      {/* Plates — outer first so the inner faces paint on top */}
      <Plate id={id} x={36} color="yellow" side="left" ry={30} />
      <Plate id={id} x={45} color="blue" side="left" />
      <Plate id={id} x={54} color="red" side="left" />
      <Plate id={id} x={63} color="red" side="left" />
      <Plate id={id} x={264} color="yellow" side="right" ry={30} />
      <Plate id={id} x={255} color="blue" side="right" />
      <Plate id={id} x={246} color="red" side="right" />
      <Plate id={id} x={237} color="red" side="right" />
      {/* Collars */}
      <rect
        x="68"
        y={BAR_Y - 6}
        width="5"
        height="12"
        rx="1.5"
        fill="#1b1d22"
      />
      <rect
        x="227"
        y={BAR_Y - 6}
        width="5"
        height="12"
        rx="1.5"
        fill="#1b1d22"
      />

      {/* Floor props: hex dumbbell (left), kettlebell (right) */}
      <g>
        <ellipse cx="34" cy="206" rx="30" ry="4" fill="#000" opacity="0.55" />
        <rect
          x="16"
          y="198"
          width="36"
          height="4"
          rx="2"
          fill={`url(#${id("chrome")})`}
        />
        <polygon
          points="6,190 16,186 22,192 22,206 12,210 6,204"
          fill="#1d1f24"
        />
        <polygon
          points="46,192 52,186 62,190 62,204 56,210 46,206"
          fill="#1d1f24"
        />
        <path
          d="M6 190 L16 186 L22 192 M46 192 L52 186 L62 190"
          stroke="#6b707a"
          strokeWidth="0.9"
          fill="none"
        />
      </g>
      <g>
        <ellipse
          cx="270"
          cy="210"
          rx="18"
          ry="3.5"
          fill="#000"
          opacity="0.55"
        />
        <path
          d="M262 188 Q260 172 270 172 Q280 172 278 188"
          stroke="#1a1b20"
          strokeWidth="5"
          fill="none"
        />
        <circle cx="270" cy="197" r="13" fill={`url(#${id("iron")})`} />
        <path
          d="M260 193 Q270 189 280 193"
          stroke="#d1172b"
          strokeWidth="2"
          fill="none"
        />
      </g>
    </svg>
  );
}

export function WeightTrainingDisplayPreview({
  participants,
}: WeightTrainingDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Weight Training"
      backgroundClassName="bg-[#0d0e11]"
      participants={participants}
      scene={
        <>
          <style href="weight-training-scene-keyframes" precedence="default">
            {WEIGHT_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Back wall: concrete panels under a warm spot */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(55% 75% at 50% 0%, rgba(255,236,206,0.42) 0%, rgba(255,236,206,0.1) 50%, transparent 75%), linear-gradient(180deg, #23252b 0%, #1b1c21 60%, #111215 75%, #0c0d0f 100%)",
            }}
          />
          <div className="absolute inset-x-0 top-0 h-[75%] bg-[repeating-linear-gradient(90deg,transparent_0_63px,rgba(0,0,0,0.35)_63px_64px,rgba(255,255,255,0.04)_64px_65px)]" />
          {/* Red neon strip */}
          <div className="absolute inset-x-[6%] top-[54px] h-[3px] rounded-full bg-[#ff4d5e] shadow-[0_0_10px_2px_rgba(255,60,80,0.75),0_0_34px_8px_rgba(255,40,60,0.3)]" />
          {/* Floor */}
          <div className="absolute inset-x-0 bottom-0 h-[25%] bg-[linear-gradient(180deg,#17181c_0%,#0b0b0d_100%)]">
            <div className="absolute inset-x-0 top-0 h-px bg-white/10" />
          </div>

          <Rack id={id} />

          {/* Chalk in the light */}
          {CHALK.map((mote) => (
            <span
              key={`chalk-${mote.left}-${mote.top}`}
              className="wt-chalk absolute rounded-full bg-white/80"
              style={{
                left: mote.left,
                top: mote.top,
                width: mote.size,
                height: mote.size,
                animationDelay: mote.delay,
              }}
            />
          ))}
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_115%_120%_at_50%_40%,transparent_50%,rgba(0,0,0,0.6)_100%)]" />
      }
    />
  );
}
